import dayjs from "dayjs";
import { Link } from "react-router-dom";
import {
  ActionIcon,
  Button,
  Divider,
  Drawer,
  Group,
  Menu,
  Paper,
  Stack,
  Switch,
  Text,
} from "@mantine/core";
import {
  IconDots,
  IconMailForward,
  IconPencil,
  IconPrinter,
  IconRefresh,
  IconTrash,
  IconUserMinus,
} from "@tabler/icons-react";

import {
  useDeleteTicket,
  useRotateClaimCode,
  useUnclaimTicket,
  useUpdateTicket,
} from "../../api/tickets";
import { useConfirm } from "../../hooks/useConfirm";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";
import { getClaimAccount, isTicketClaimed } from "../../utils/ticketClaim";
import {
  rotateConfirmation,
  confirmUnclaim,
  unclaimBody,
} from "../Tickets/ClaimStatus";
import { openSendCampaignModal } from "../Tickets/SendCampaignModal";
import EmailTicketButton, {
  TicketEmailState,
} from "../Tickets/EmailTicketButton";
import {
  useHasAvailableCampaigns,
  usePrintTicket,
} from "../Tickets/ticketRowActions";
import {
  CopyLinkButton,
  STATUS,
  StatusBadge,
  TicketCode,
  fullName,
  getAppStatus,
} from "./ticketParts";

/**
 * One ticket, opened from a row of the tickets preview: where it is in the
 * app and the one thing to do about it, check-in, and who it is for. The rare
 * and destructive actions wait behind "More".
 */
function TicketPanel({ ticket, eventId, onClose, checkInFirst = false }) {
  return (
    <Drawer
      opened={Boolean(ticket)}
      onClose={onClose}
      position="right"
      size={440}
      title={
        ticket && (
          <Text fw={600} size="lg" lineClamp={2}>
            {fullName(ticket) || "No name"}
          </Text>
        )
      }
    >
      {ticket && (
        <TicketPanelBody
          ticket={ticket}
          eventId={eventId}
          onClose={onClose}
          checkInFirst={checkInFirst}
        />
      )}
    </Drawer>
  );
}

function TicketPanelBody({ ticket, eventId, onClose, checkInFirst }) {
  return (
    <Stack gap="lg">
      {checkInFirst && <CheckIn ticket={ticket} />}
      <AppAccess ticket={ticket} />
      {!checkInFirst && <CheckIn ticket={ticket} />}
      <Holder ticket={ticket} />
      <Divider />
      <Group justify="space-between">
        <Button
          variant="default"
          size="xs"
          leftSection={<IconPencil size={14} />}
          component={Link}
          to={`/app/tickets/${ticket.id}`}
        >
          Edit details
        </Button>
        <MoreMenu ticket={ticket} eventId={eventId} onDeleted={onClose} />
      </Group>
    </Stack>
  );
}

// Where the ticket is in the app, and the one action that fits.
function AppAccess({ ticket }) {
  const status = getAppStatus(ticket);
  const account = getClaimAccount(ticket);
  const { mutate: unclaim, isPending: isUnclaiming } = useUnclaimTicket({
    ticketId: ticket.id,
  });

  return (
    // Focus lands here rather than on the close button.
    <Paper
      withBorder
      radius="md"
      p="md"
      tabIndex={-1}
      data-autofocus
      style={{ outline: "none" }}
    >
      <Stack gap="sm">
        <Group gap="xs">
          <StatusBadge ticket={ticket} />
        </Group>
        <Text size="sm">{STATUS[status].sentence}</Text>

        {status === "inApp" && account && (
          <Text size="sm" c="dimmed">
            {[account.name, account.email].filter(Boolean).join(", ")}
          </Text>
        )}

        {status === "notAdded" && (
          <>
            <Group gap="xs">
              <CopyLinkButton ticket={ticket} />
              <EmailTicketButton ticket={ticket} />
            </Group>
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                Code
              </Text>
              <TicketCode ticket={ticket} />
            </Group>
          </>
        )}

        <TicketEmailState ticket={ticket} />

        {status === "lockedOut" && (
          <Group>
            <Button
              variant="light"
              color="red"
              size="xs"
              leftSection={<IconUserMinus size={14} />}
              loading={isUnclaiming}
              onClick={() =>
                confirmUnclaim({
                  account,
                  onConfirm: (newCode) => unclaim(unclaimBody(newCode)),
                })
              }
            >
              Take out of app
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}

function CheckIn({ ticket }) {
  const { mutate, isPending } = useUpdateTicket({ ticketId: ticket.id });
  const checkedIn = Boolean(ticket.checkedInAt);

  return (
    <Group justify="space-between" wrap="nowrap">
      <div>
        <Text size="sm" fw={500}>
          Checked in
        </Text>
        <Text size="xs" c="dimmed">
          {checkedIn
            ? dayjs(ticket.checkedInAt).format("DD.MM.YYYY. HH:mm")
            : "Not yet"}
        </Text>
      </div>
      <Switch
        color="green"
        size="md"
        checked={checkedIn}
        disabled={isPending}
        aria-label="Checked in"
        onChange={() =>
          mutate({
            checkedInAt: checkedIn ? null : new Date().toISOString(),
            checkInMethod: "hand",
          })
        }
      />
    </Group>
  );
}

function Holder({ ticket }) {
  const rows = [
    ["Email", ticket.email],
    ["Company", ticket.companyName],
  ];
  return (
    <Stack gap={6}>
      {rows.map(([label, value]) => (
        <Group key={label} gap="sm" wrap="nowrap" align="flex-start">
          <Text size="sm" c="dimmed" w={72} style={{ flexShrink: 0 }}>
            {label}
          </Text>
          <Text
            size="sm"
            c={value ? undefined : "dimmed"}
            style={{ wordBreak: "break-word" }}
          >
            {value || "None"}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}

// The rare ones: print, a campaign, a new code, take out, delete.
function MoreMenu({ ticket, eventId, onDeleted }) {
  const confirm = useConfirm();
  const status = getAppStatus(ticket);
  const isClaimed = isTicketClaimed(ticket);
  const account = getClaimAccount(ticket);
  const handlePrint = usePrintTicket(eventId);
  const hasAvailableCampaigns = useHasAvailableCampaigns(eventId);
  const { mutate: rotateCode } = useRotateClaimCode({ ticketId: ticket.id });
  const { mutate: unclaim } = useUnclaimTicket({ ticketId: ticket.id });
  const { mutate: deleteTicket } = useDeleteTicket();
  const confirmDelete = useDeleteConfirm("ticket", deleteTicket);

  return (
    <Menu position="top-end" withinPortal>
      <Menu.Target>
        <ActionIcon variant="default" size="lg" aria-label="More">
          <IconDots size={18} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconPrinter size={16} />}
          onClick={() => handlePrint(ticket)}
        >
          Print badge
        </Menu.Item>
        {/* Not added has "Email the ticket" up front. */}
        {status !== "notAdded" && (
          <Menu.Item
            leftSection={<IconMailForward size={16} />}
            disabled={!ticket.email || !hasAvailableCampaigns}
            onClick={() => openSendCampaignModal({ eventId, ticket })}
          >
            Send a campaign
          </Menu.Item>
        )}
        <Menu.Divider />
        <Menu.Item
          leftSection={<IconRefresh size={16} />}
          onClick={() =>
            confirm({
              ...rotateConfirmation(isClaimed),
              onConfirm: () => rotateCode({}),
            })
          }
        >
          New ticket code
        </Menu.Item>
        {/* Locked out has it up front. */}
        {isClaimed && status !== "lockedOut" && (
          <Menu.Item
            leftSection={<IconUserMinus size={16} />}
            onClick={() =>
              confirmUnclaim({
                account: account,
                onConfirm: (newCode) => unclaim(unclaimBody(newCode)),
              })
            }
          >
            Take out of app
          </Menu.Item>
        )}
        <Menu.Item
          color="red"
          leftSection={<IconTrash size={16} />}
          onClick={() =>
            confirmDelete({ id: ticket.id }, { onSuccess: onDeleted })
          }
        >
          Delete ticket
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

export default TicketPanel;
